// Utility functions for All Interchanges tab data processing

/**
 * Normalize field names to handle different API response formats
 * @param {Object} item - Data item with potentially different field names
 * @returns {Object} - Normalized item with consistent field names
 */
const normalizeSalesItem = (item) => {
  return {
    region: item.region || item.Region || item.REGION,
    interchangeNumber: item.interchangeNumber || item.interchange_number || item.InterchangeNumber || item.INTERCHANGE_NUMBER,
    invoiceDate: item.invoiceDate || item.invoice_date || item.InvoiceDate || item.INVOICE_DATE,
    allPartsGrossSales: item.allPartsGrossSales || item.all_parts_gross_sales || item.AllPartsGrossSales || item.ALL_PARTS_GROSS_SALES
  };
};

/**
 * Normalize field names to handle different API response formats
 * @param {Object} item - Data item with potentially different field names
 * @returns {Object} - Normalized item with consistent field names
 */
const normalizeStockItem = (item) => {
  return {
    region: item.region || item.Region || item.REGION,
    interchangeNumber: item.interchangeNumber || item.interchange_number || item.InterchangeNumber || item.INTERCHANGE_NUMBER,
    stockStatus: item.stockStatus || item.stock_status || item.StockStatus || item.STOCK_STATUS,
    partName: item.partName || item.part_name || item.PartName || item.PART_NAME
  };
};

/**
 * Calculate 90-day sales summary from sales data
 * @param {Array} salesData - Array of sales records
 * @returns {Object} - Sales summary by region and interchange
 */
export const calculateSalesSummary = (salesData) => {
  // Use UTC dates to avoid timezone issues - match R's Sys.Date() behavior
  const today = new Date();
  const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const ninetyDaysAgo = new Date(todayUTC.getTime() - 90 * 24 * 60 * 60 * 1000);
  
  console.log('Sales calculation debug:', {
    today: today.toISOString(),
    todayUTC: todayUTC.toISOString(),
    ninetyDaysAgo: ninetyDaysAgo.toISOString(),
    totalSalesRecords: salesData?.length || 0
  });
  
  const normalizedData = salesData.map(normalizeSalesItem);
  
  const filteredData = normalizedData.filter(sale => {
    // Handle different date formats and timezone issues
    let saleDate;
    const dateStr = sale.invoiceDate;
    
    if (!dateStr) return false;
    
    // If date has time component (ISO format), extract just the date part
    if (dateStr.includes('T')) {
      const dateOnly = dateStr.split('T')[0];
      saleDate = new Date(dateOnly + 'T00:00:00.000Z');
    } else {
      // If it's just a date string, parse it as UTC
      saleDate = new Date(dateStr + 'T00:00:00.000Z');
    }
    
    // Check if date is valid
    if (isNaN(saleDate.getTime())) {
      console.warn('Invalid date format:', dateStr);
      return false;
    }
    
    return saleDate >= ninetyDaysAgo;
  });

  console.log('Sales calculation debug - filtered records:', {
    filteredCount: filteredData.length,
    sampleDates: filteredData.slice(0, 3).map(s => s.invoiceDate)
  });

  const summary = filteredData.reduce((acc, sale) => {
    const key = `${sale.region}_${sale.interchangeNumber}`;
    if (!acc[key]) {
      acc[key] = {
        region: sale.region,
        interchangeNumber: sale.interchangeNumber,
        sales: 0,
        count: 0
      };
    }
    const salesAmount = parseFloat(sale.allPartsGrossSales) || 0;
    acc[key].sales += salesAmount;
    acc[key].count += 1;
    return acc;
  }, {});
  
  // Debug: Log summary for first few entries
  const summaryEntries = Object.entries(summary).slice(0, 5);
  console.log('Sales summary debug - first 5 entries:', summaryEntries);
  
  return summary;
};

/**
 * Process stock status data to create summary table
 * @param {Array} stockStatusData - Array of stock status records
 * @returns {Array} - Summary table data
 */
export const processStockStatusSummary = (stockStatusData) => {
  const engineStock = stockStatusData
    .map(normalizeStockItem)
    .filter(item => item.partName === 'ENGINE');
  
  // Count by status and region
  const statusCounts = engineStock.reduce((acc, item) => {
    const key = `${item.region}_${item.stockStatus}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // Calculate totals by region
  const regionTotals = engineStock.reduce((acc, item) => {
    acc[item.region] = (acc[item.region] || 0) + 1;
    return acc;
  }, {});

  // Create summary table data
  const statuses = ['Understocked', 'Adequately Stocked', 'Overstocked'];
  const regions = ['MAR', 'MWR', 'SER', 'SWR'];
  
  const summaryData = statuses.map(status => {
    const row = { stockStatus: status };
    regions.forEach(region => {
      const count = statusCounts[`${region}_${status}`] || 0;
      const total = regionTotals[region] || 0;
      const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
      row[region] = `${count} (${percentage}%)`;
    });
    return row;
  });

  // Add totals row
  const totalsRow = { stockStatus: 'Total' };
  regions.forEach(region => {
    totalsRow[region] = regionTotals[region] || 0;
  });
  summaryData.push(totalsRow);

  return summaryData;
};

/**
 * Process all interchanges data combining stock status and sales
 * @param {Array} stockStatusData - Array of stock status records
 * @param {Object} salesSummary - Sales summary object
 * @returns {Array} - Processed interchanges data
 */
export const processAllInterchangesData = (stockStatusData, salesSummary) => {
  // Remove the ENGINE filter to match R app behavior - R app shows ALL stock status records
  const allStock = stockStatusData
    .map(normalizeStockItem);
  // .filter(item => item.partName === 'ENGINE'); // COMMENTED OUT: R app doesn't filter by part name for main table
  
  console.log('Stock status processing debug:', {
    totalStockRecords: stockStatusData?.length || 0,
    normalizedStockRecords: allStock.length,
    sampleStockData: allStock.slice(0, 3),
    salesSummaryKeys: Object.keys(salesSummary || {}).slice(0, 5)
  });
  
  // Create a map of stock status by region and interchange
  const stockStatusMap = {};
  allStock.forEach(item => {
    const key = `${item.interchangeNumber}_${item.region}`;
    stockStatusMap[key] = item.stockStatus;
  });

  // Get unique interchange numbers
  const uniqueInterchanges = [...new Set(allStock.map(item => item.interchangeNumber))];
  
  console.log('Stock status processing debug - unique interchanges:', {
    uniqueInterchangeCount: uniqueInterchanges.length,
    sampleInterchanges: uniqueInterchanges.slice(0, 5)
  });

  const result = uniqueInterchanges.map(interchangeNumber => {
    const regions = ['MAR', 'MWR', 'SER', 'SWR'];
    const row = { interchangeNumber };
    
    regions.forEach(region => {
      const status = stockStatusMap[`${interchangeNumber}_${region}`] || null;
      const salesKey = `${region}_${interchangeNumber}`;
      const sales = salesSummary[salesKey]?.sales || 0;
      
      row[`${region}Status`] = status;
      row[`${region}Sales`] = sales;
    });

    // Calculate total sales
    row.totalSales = regions.reduce((sum, region) => {
      return sum + (row[`${region}Sales`] || 0);
    }, 0);

    return row;
  });
  
  // Debug: Log first few results
  console.log('All interchanges processing debug - first 3 results:', 
    result.slice(0, 3).map(r => ({
      interchange: r.interchangeNumber,
      marSales: r.MARSales,
      mwrSales: r.MWRSales,
      serSales: r.SERSales,
      swrSales: r.SWRSales,
      totalSales: r.totalSales
    }))
  );
  
  return result;
};

/**
 * Filter and sort interchanges data
 * @param {Array} data - Interchanges data
 * @param {string} filterText - Filter text
 * @param {Object} sortConfig - Sort configuration
 * @returns {Array} - Filtered and sorted data
 */
export const filterAndSortData = (data, filterText, sortConfig) => {
  let filtered = [...data];
  
  // Apply filter
  if (filterText) {
    filtered = filtered.filter(item => 
      item.interchangeNumber.toString().includes(filterText) ||
      Object.values(item).some(val => 
        val && val.toString().toLowerCase().includes(filterText.toLowerCase())
      )
    );
  }

  // Apply sorting
  filtered.sort((a, b) => {
    const aValue = a[sortConfig.key] || 0;
    const bValue = b[sortConfig.key] || 0;
    
    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  return filtered;
};

/**
 * Get status badge color class
 * @param {string} status - Stock status
 * @returns {string} - CSS class name
 */
export const getStatusColor = (status) => {
  if (!status) return 'status_na';
  if (status.toLowerCase().includes('understocked')) return 'status_understock';
  if (status.toLowerCase().includes('adequately stocked')) return 'status_adeq_stock';
  if (status.toLowerCase().includes('overstocked')) return 'status_overstock';
  return 'status_na';
};

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

/**
 * Validate and clean sales data
 * @param {Array} salesData - Raw sales data
 * @returns {Array} - Cleaned sales data
 */
export const validateSalesData = (salesData) => {
  if (!Array.isArray(salesData)) {
    return [];
  }
  
  // Check if we have any data
  if (salesData.length === 0) {
    return [];
  }
  
  const filtered = salesData.filter(sale => {
    if (!sale || typeof sale !== 'object') {
      return false;
    }
    
    // Use the normalize function to check if all required fields exist
    const normalized = normalizeSalesItem(sale);
    const hasRegion = normalized.region;
    const hasInterchange = normalized.interchangeNumber;
    const hasDate = normalized.invoiceDate;
    const hasSales = normalized.allPartsGrossSales !== undefined;
    
    const isValid = hasRegion && hasInterchange && hasDate && hasSales;
    
    return isValid;
  });
  
  return filtered;
};

/**
 * Validate and clean stock status data
 * @param {Array} stockData - Raw stock data
 * @returns {Array} - Cleaned stock data
 */
export const validateStockData = (stockData) => {
  if (!Array.isArray(stockData)) {
    return [];
  }
  
  // Check if we have any data
  if (stockData.length === 0) {
    return [];
  }
  
  const filtered = stockData.filter(item => {
    if (!item || typeof item !== 'object') {
      return false;
    }
    
    // Use the normalize function to check if all required fields exist
    const normalized = normalizeStockItem(item);
    const hasRegion = normalized.region;
    const hasInterchange = normalized.interchangeNumber;
    const hasStatus = normalized.stockStatus;
    const hasPartName = normalized.partName;
    
    const isValid = hasRegion && hasInterchange && hasStatus && hasPartName;
    
    return isValid;
  });
  
  return filtered;
}; 